import { groupBy, sortBy } from 'lodash-es';
import { DBDateFormat, ZERO_AMOUNT } from 'src/lib/constants';
import dayjs from 'dayjs';
import { Option, Price, Nationality, AgeType, EnumPriceUnit, Transport } from 'src/types';
import { generateArrayDates } from 'src/helpers';

export const PRICE_ZERO: Price = {
  id: 0,
  supplierActivityOptionId: 0,
  minQtyForPrice: 1,
  maxQtyForPrice: null,
  price: ZERO_AMOUNT,
  createdAt: '2022-01-01',
  updatedAt: '2022-01-01',
  agentPrice: ZERO_AMOUNT,
  accommodationRoomId: null,
  masterPrice: ZERO_AMOUNT,
  masterPriceCents: 0,
  masterPriceCurrency: 'SGD',
  ageType: 'adult',
  nationality: 'foreigner',
  tripStartDate: null,
  tripEndDate: null,
  discounted: false,
  weekdays: null,
  priceGroupId: 0,
  excludedDates: [],
  localId: null,
  localPrice: null,
  localAgentPrice: null,
  crossOutPrice: null,
  localCrossOutPrice: null,
  priceGroupKind: 'basic',
  hasDiscountSmallGroups: false,
  applicableDates: []
};

const nextTwoMonthDates = generateArrayDates(dayjs().toDate(), dayjs().add(2, 'month').toDate())

const selectSeasonalPrices = (prices: Price[]) => {
  const seasonalPrices = prices.filter(o => o.priceGroupKind !== 'basic')
  if (seasonalPrices.length > 0) {
    const anyDateWithoutSeasonalPrice = nextTwoMonthDates.some((date) => {
      const hasSeasonalPrice = seasonalPrices.find((price) => {
        return price.applicableDates.includes(date)
      })
      return !hasSeasonalPrice
    })
    prices = anyDateWithoutSeasonalPrice ? prices : seasonalPrices
  }
}

export function calculateTotalTransportPrice(transport: Transport, qty: number) {
  const num = transport.capacity || 1
  return Math.ceil(qty / num) * Number(transport.amount.amount)
}

export function resolvedPrices(
  initialPrices: Price[],
  accommodationRoomId: number | null,
  dateStr: string | null,
  nationality: Nationality,
  ageType: AgeType,
): Price[] {
  let iPrices = initialPrices;
  let prices = dateStr
    ? iPrices.filter(function (price) {
        return price.applicableDates.includes(dateStr)
      })
    : [...iPrices]

  if (accommodationRoomId) {
    prices = prices.filter(function (price) {
      return price.accommodationRoomId === accommodationRoomId;
    });
  }

  prices = prices.filter(function (price) {
    return price.ageType === ageType;
  });

  let resolvedPrices = prices.filter(function (price) {
    return price.nationality === nationality;
  });

  if (resolvedPrices.length === 0 && nationality === 'local') {
    resolvedPrices = prices.filter(function (price) {
      return price.nationality === 'foreigner';
    });
  }

  return resolvedPrices
}

export function resolvePrice(
  initialPrices: Price[],
  initialOption: Option,
  accommodationRoomId: number | null,
  dateStr: string | null,
  nationality: Nationality,
  ageType: AgeType,
  qty: number | null
): Price {
  let option = initialOption;
  let resolvedPrice: Price = PRICE_ZERO;
  let prices = resolvedPrices(initialPrices, accommodationRoomId, dateStr, nationality, ageType)
  const seasonalSortingValue = (price: Price) => {
    if (dateStr) {
      return price.priceGroupKind === 'basic' ? 1 : 0
    } else {
      return 1
    }
  }

  if (qty && qty > 0) {
    if (option.priceUnit === EnumPriceUnit.perGroup || option.priceUnit === EnumPriceUnit.perRoom) {
      let groupResolvedPrices = prices.filter(function (price) {
        return price.maxQtyForPrice && price.maxQtyForPrice >= qty && price.minQtyForPrice <= qty;
      });

      groupResolvedPrices = groupResolvedPrices.length > 0 ? groupResolvedPrices : prices;

      let groupedPrices = groupBy(groupResolvedPrices, function (price) {
        return price.minQtyForPrice - qty < 1 ? 'low' : 'high';
      });

      const lowPrices = groupedPrices.low || [];
      const highPrices = groupedPrices.high || [];
      resolvedPrice = sortBy(lowPrices, [seasonalSortingValue, (price) => -price.minQtyForPrice, 'masterPriceCents'])[0] ||
        sortBy(highPrices, [seasonalSortingValue, 'minQtyForPrice', 'masterPriceCents'])[0];
    } else if (option.priceUnit === 'per_person') {
      let counter = 1;
      let tmpQty = qty;
      while (counter <= 5) {
        resolvedPrice = sortBy(
          prices.filter(function (price) {
            return price.minQtyForPrice <= tmpQty;
          }),
          [seasonalSortingValue, (price) => -price.minQtyForPrice, 'masterPriceCents']
        )[0];
        if (resolvedPrice) break;
        counter += 1;
        tmpQty += 1;
      }
    }
  } else {
    resolvedPrice = sortBy(prices, [seasonalSortingValue, 'masterPriceCents'])[0];
  }

  return resolvedPrice || PRICE_ZERO;
}

export function calculateTotalPrice(
  initialPrices: Price[],
  initialOption: Option,
  accommodationRoomId: number | null,
  dateStr: string,
  initialQty: {
    quantity: number;
    localQuantity: number;
    childQuantity: number;
    childLocalQuantity: number;
  }
) {
  let prices = initialPrices;
  let option = initialOption;
  let qty = initialQty;
  let totalPrice = 0;
  let resolvedPriceAdult;
  let resolvedPriceAdultLocal;
  let numAdult = qty.quantity;
  let numAdultLocal = qty.localQuantity;

  if (option.priceUnit === EnumPriceUnit.perRoom) {
    totalPrice = Number(
      resolvePrice(prices, option, accommodationRoomId, dateStr, 'foreigner', 'adult', numAdult || null)
        .price.amount
    );
  } else if (option.priceUnit === EnumPriceUnit.perGroup) {
    let totalQty = numAdult + numAdultLocal;
    if (!numAdult && !numAdultLocal) {
      totalPrice = Number(
        resolvePrice(prices, option, accommodationRoomId, dateStr, 'foreigner', 'adult', null)
          .price.amount
      );
    } else {
      if (numAdult && numAdult > 0) {
        resolvedPriceAdult = resolvePrice(
          prices,
          option,
          accommodationRoomId,
          dateStr,
          'foreigner',
          'adult',
          totalQty
        );
        totalPrice += resolvedPriceAdult
          ? (Number(resolvedPriceAdult.price.amount) / totalQty) * numAdult
          : 0;
      }

      if (numAdultLocal && numAdultLocal > 0) {
        resolvedPriceAdultLocal = resolvePrice(
          prices,
          option,
          accommodationRoomId,
          dateStr,
          'local',
          'adult',
          totalQty
        );
        totalPrice += resolvedPriceAdultLocal
          ? (Number(resolvedPriceAdultLocal.price.amount) / totalQty) * numAdultLocal
          : 0;
      }

      totalPrice;
    }
  } else {
    let resolvedPriceChild;
    let resolvedPriceChildLocal;
    let numChild = qty.childQuantity;
    let numChildLocal = qty.childLocalQuantity;
    let totalQty = numAdult + numAdultLocal + numChild + numChildLocal;

    if (!numAdult && !numAdultLocal && !numChild && !numChildLocal) {
      totalPrice = Number(
        resolvePrice(prices, option, accommodationRoomId, dateStr, 'foreigner', 'adult', null)
          .price.amount
      );
    } else {
      if (numAdult > 0) {
        resolvedPriceAdult = resolvePrice(
          prices,
          option,
          accommodationRoomId,
          dateStr,
          'foreigner',
          'adult',
          numAdult
        );
        totalPrice += resolvedPriceAdult
          ? Number(resolvedPriceAdult.price.amount) * numAdult
          : 0;
      }

      if (numChild > 0) {
        resolvedPriceChild = resolvePrice(
          prices,
          option,
          accommodationRoomId,
          dateStr,
          'foreigner',
          'child',
          numChild
        );
        totalPrice += resolvedPriceChild
          ? Number(resolvedPriceChild.price.amount) * numChild
          : 0;
      }

      if (numAdultLocal > 0) {
        resolvedPriceAdultLocal = resolvePrice(
          prices,
          option,
          accommodationRoomId,
          dateStr,
          'local',
          'adult',
          numAdultLocal
        );
        totalPrice += resolvedPriceAdultLocal
          ? Number(resolvedPriceAdultLocal.price.amount) * numAdultLocal
          : 0;
      }

      if (numChildLocal > 0) {
        resolvedPriceChildLocal = resolvePrice(
          prices,
          option,
          accommodationRoomId,
          dateStr,
          'local',
          'child',
          numChildLocal
        );
        totalPrice += resolvedPriceChildLocal
          ? Number(resolvedPriceChildLocal.price.amount) * numChildLocal
          : 0;
      }
    }
  }

  return totalPrice;
}
